import {state} from "./state";

/* ===============================
   LOAD EXISTING ANIMATED IMAGES
=============================== */
export function loadAnimatedImages(images) {
    state.animatedImages = {};
    state.animatedImagesGroup.clearLayers();

    images.forEach(img => {
        const marker = createMarker({
            id: img.id,
            backendId: img.id,
            lat: img.lat,
            lng: img.lng,
            url: img.asset.image,
            width: img.asset.width,
            height: img.asset.height,
            assetId: img.asset.id,
            status: "ready"
        });

        state.animatedImages[img.id] = marker;
    });

    syncAnimatedImagesDragState();
}

/* ===============================
   CREATE IMAGE ON MAP (UI FIRST)
=============================== */
export function createAnimatedImage(asset, lat, lng) {
    const tmpId = `tmp-${Date.now()}`;

    const img = {
        id: tmpId,
        backendId: null,
        lat,
        lng,
        url: asset.image,
        width: asset.width,
        height: asset.height,
        assetId: asset.id,
        status: "creating"
    };

    const marker = createMarker(img);
    state.animatedImages[tmpId] = marker;

    htmx.trigger("#map-adventure", "map:create-animated-image", {
        tmpId,
        asset: asset.id,
        lat,
        lng
    });

    return marker;
}

function createDivIconFromImg(img) {
    return L.divIcon({
        className: "map-animated-image",
        html: `
            <img src="${img.url}"
                 style="width:${img.width}px;
                        height:${img.height}px;
                        pointer-events:none;">
        `,
        iconSize: [img.width, img.height],
        iconAnchor: [img.width / 2, img.height / 2]
    });
}

/* ===============================
   INTERNAL: CREATE MARKER
=============================== */
function createMarker(img) {
    const marker = L.marker([img.lat, img.lng], {
        icon: createDivIconFromImg(img),
        draggable: true,
        interactive: true,
        zIndexOffset: -1000
    }).addTo(state.animatedImagesGroup);

    marker.options.imageData = img;

    enableAnimatedImageDrag(marker);
    enableAnimatedImageDelete(marker);

    return marker;
}

/* ===============================
   DRAG HANDLING
=============================== */
function enableAnimatedImageDrag(marker) {
    marker.on("dragstart", () => {
        state.map.dragging.disable();
    });

    marker.on("dragend", e => {
        state.map.dragging.enable();

        const img = marker.options.imageData;

        if (!img.backendId) return;

        const {lat, lng} = e.target.getLatLng();
        img.lat = lat;
        img.lng = lng;

        htmx.trigger("#map-adventure", "map:move-animated-image", {
            id: img.backendId,
            lat,
            lng
        });
    });
}

/* ===============================
   RIGHT CLICK → DELETE
=============================== */
function enableAnimatedImageDelete(marker) {
    marker.on("contextmenu", () => {
        if (!state.editMode) return
        const img = marker.options.imageData;

        marker.remove();
        delete state.animatedImages[img.id];

        if (img.backendId) {
            htmx.trigger("#map-adventure", "map:delete-animated-image", {
                id: img.backendId
            });
        }
    });
}

/* ===============================
   BACKEND CONFIRMATION (CREATE)
=============================== */
export function confirmAnimatedImageCreated(tmpId, backendData) {
    const marker = state.animatedImages[tmpId];
    if (!marker) return;

    const img = marker.options.imageData;

    delete state.animatedImages[tmpId];

    img.id = backendData.id;
    img.backendId = backendData.id;
    img.status = "ready";

    state.animatedImages[img.backendId] = marker;

    if (state.editMode && marker.dragging) {
        marker.dragging.enable();
    }
}

/* ===============================
   DRAG & DROP FROM ASSET LIST
   ⚠️ MUST STAY HERE
=============================== */
export function enableAssetDragToMap() {
    let ghost = null;

    const assetListContainer = document.getElementById("asset_list");
    if (!assetListContainer) return;

    assetListContainer.addEventListener("dragstart", e => {
        const assetEl = e.target.closest(".asset-card");
        if (!assetEl) return;

        const assetData = {
            id: parseInt(assetEl.dataset.assetId),
            image: assetEl.querySelector("img").src,
            width: parseInt(assetEl.dataset.assetWidth || 64),
            height: parseInt(assetEl.dataset.assetHeight || 64)
        };

        e.dataTransfer.setData("application/json", JSON.stringify(assetData));

        const emptyImg = new Image();
        emptyImg.src = "";
        e.dataTransfer.setDragImage(emptyImg, 0, 0);

        ghost = document.createElement("img");
        ghost.src = assetData.image;
        ghost.style.position = "absolute";
        ghost.style.width = assetData.width + "px";
        ghost.style.height = assetData.height + "px";
        ghost.style.pointerEvents = "none";
        ghost.style.opacity = "0.6";
        ghost.style.zIndex = "9999";
        document.body.appendChild(ghost);
    });

    document.addEventListener("dragover", e => {
        e.preventDefault();
        if (ghost) {
            ghost.style.left = e.pageX - ghost.width / 2 + "px";
            ghost.style.top = e.pageY - ghost.height / 2 + "px";
        }
    });

    document.addEventListener("dragend", () => {
        ghost?.remove();
        ghost = null;
    });

    const mapContainer = state.map.getContainer();

    mapContainer.addEventListener("dragover", e => e.preventDefault());

    mapContainer.addEventListener("drop", e => {
        e.preventDefault();
        if (!ghost) return;

        const asset = JSON.parse(
            e.dataTransfer.getData("application/json")
        );

        const rect = mapContainer.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const latlng = state.map.containerPointToLatLng([x, y]);

        createAnimatedImage(asset, latlng.lat, latlng.lng);

        ghost.remove();
        ghost = null;
    });
}

/* ===============================
   EDIT MODE SYNC
=============================== */
export function enableAnimatedImagesDrag() {
    Object.values(state.animatedImages).forEach(marker => {
        setAnimatedImageInteractive(marker, true);
        if (
            marker.dragging &&
            marker.options.imageData.backendId
        ) {
            marker.dragging.enable();
        }
    });
}

function setAnimatedImageInteractive(marker, interactive) {
    marker.options.interactive = interactive;

    const el = marker.getElement();
    if (!el) return;

    el.classList.toggle("leaflet-interactive", interactive);
    el.classList.toggle("animated-image-editable", interactive);
}

export function disableAnimatedImagesDrag() {
    Object.values(state.animatedImages).forEach(marker => {
        setAnimatedImageInteractive(marker, false);
        if (marker.dragging) {
            marker.dragging.disable();
        }
    });
}

function syncAnimatedImagesDragState() {
    if (state.editMode) enableAnimatedImagesDrag();
    else disableAnimatedImagesDrag();
}


/* ===============================
   BACKEND UPDATE (ASSET MODIFICATION)
=============================== */
export function updateAnimatedImage(assetData) {
    Object.values(state.animatedImages).forEach(marker => {
        const img = marker.options.imageData;
        if (img.assetId !== assetData.id) return;

        img.url = assetData.image;
        img.width = assetData.width;
        img.height = assetData.height;
        marker.setIcon(createDivIconFromImg(img));
    });
}

/* ===============================
   BACKEND DELETE (ASSET)
=============================== */
export function deleteAnimatedImage(assetId) {
    Object.values(state.animatedImages).forEach(marker => {
        const img = marker.options.imageData;
        if (img.assetId !== assetId) return;

        marker.remove();
        delete state.animatedImages[img.id];
    });
}
